import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntraAuth } from '../components/EntraAuth/EntraAuthProvider';
import { Card } from '../components/ui';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import RedirectOverlay from '../components/RedirectOverlay';

import './Login.css';

const Login = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useEntraAuth();

    // State for form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [step, setStep] = useState('email'); // 'email' or 'password'

    // State for messages
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // Redirect if already authenticated
    useEffect(() => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');

        if (token && user) {
            const userData = JSON.parse(user);
            if (userData.role === 'doctor') {
                navigate('/doctor-admin');
            } else {
                navigate('/');
            }
        }
    }, [navigate, isAuthenticated]);

    // Handle Entra ID authentication redirect
    useEffect(() => {
        if (isAuthenticated) {
            const msalAccounts = localStorage.getItem('msal.account.keys');
            if (msalAccounts) {
                setSuccess('Autentificare reușită!');
                setTimeout(() => {
                    window.location.href = 'https://compas.evoomedical.ro/dashboard';
                }, 2000);
            }
        }
    }, [isAuthenticated]);

    // Handle email submission
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError('Te rugăm să introduci adresa de email.');
            return;
        }

        // Check if internal employee
        if (email.endsWith('@evoomedical.ro')) {
            setLoading(true);

            // Silently redirect to login-entra page with auto-login parameters
            // The beautiful redirect overlay will show there during authentication
            navigate(`/login-entra?autoLogin=true&loginHint=${encodeURIComponent(email)}`);
        } else {
            // External user - show password field
            setStep('password');
        }
    };

    // Handle password submission
    const handlePasswordSubmit = (e) => {
        e.preventDefault();

        if (!password) {
            setError('Te rugăm să introduci parola.');
            return;
        }

        setLoading(true);
        setError('');

        // TODO: Implement external user authentication against database
        console.log("External user login:", { email, password, rememberMe });
        setSuccess('Autentificare în curs...');

        // Placeholder - will be implemented later
        setTimeout(() => {
            setLoading(false);
            setError('Autentificarea pacienților va fi implementată în curând.');
        }, 1000);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleBackToEmail = () => {
        setStep('email');
        setPassword('');
        setError('');
    };

    return (
        <div className="login-container">
            {/* Only show redirect overlay for already authenticated users going to Compas */}
            <RedirectOverlay
                show={!!success && success.includes('Autentificare reușită') && isAuthenticated}
                userName={email.split('@')[0] || 'utilizator'}
                message="Vă redirecționăm către Evoo Medical Compas..."
            />
            <div className="login-card">
                <div className="login-left" aria-hidden="true">
                    <div className="brand-panel">
                        <h2>Platformă sigură pentru servicii medicale</h2>
                        <p>Accesați programări, dosare și resurse medicale într-un singur loc.</p>
                    </div>
                </div>

                <div className="login-right">
                    <div className="login-header">
                    </div>

                    {error && (
                        <Card className="alert alert-error" style={{ border: 'none', marginBottom: '0.75rem' }}>
                            {error}
                        </Card>
                    )}
                    {success && (
                        <Card className="alert alert-success" style={{ border: 'none', marginBottom: '0.75rem' }}>
                            {success}
                        </Card>
                    )}

                    <div className="login-form position-relative">
                        <h3 className="artistic-title">Intră în cont</h3>
                        {step === 'email' ? (
                            <form className="k-form k-form-md" onSubmit={handleEmailSubmit}>
                                <div className="form-field-container">
                                    <div className={`k-floating-label-container ${!email ? 'k-empty' : ''}`}>
                                        <input
                                            type="email"
                                            autoComplete="email"
                                            name="username"
                                            id="input-username"
                                            className="autofill k-input k-input-md k-rounded-md k-input-solid"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder=" "
                                            disabled={loading}
                                        />
                                        <label htmlFor="input-username" className="k-label">E-mail</label>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="k-button k-button-md k-button-solid k-button-solid-primary k-rounded-md login-submit-btn"
                                    disabled={loading || !email}
                                >
                                    <span className="k-button-text">{loading ? 'Se încarcă...' : 'Continuă'}</span>
                                </button>
                            </form>
                        ) : (
                            <form className="k-form k-form-md" onSubmit={handlePasswordSubmit}>
                                <div className="form-field-container">
                                    <div className="k-floating-label-container">
                                        <input
                                            type="email"
                                            value={email}
                                            disabled
                                            className="autofill k-input k-input-md k-rounded-md k-input-solid"
                                            style={{ backgroundColor: '#f5f5f5' }}
                                        />
                                        <label className="k-label">E-mail</label>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleBackToEmail}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#667eea',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            marginTop: '0.25rem'
                                        }}
                                    >
                                        Schimbă email-ul
                                    </button>
                                </div>

                                <div className="form-field-container">
                                    <div className={`k-floating-label-container ${!password ? 'k-empty' : ''}`}>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            autoComplete="current-password"
                                            name="password"
                                            id="input-password"
                                            className="autofill k-input k-input-md k-rounded-md k-input-solid"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder=" "
                                            disabled={loading}
                                            autoFocus
                                        />
                                        <label htmlFor="input-password" className="k-label">Parola</label>
                                        <button
                                            type="button"
                                            className="button-eye-icon showPassIcon"
                                            onClick={togglePasswordVisibility}
                                            tabIndex="-1"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </button>
                                    </div>
                                </div>

                                <div className="align-justify">
                                    <div className="checkbox-wrapper">
                                        <span className="k-checkbox-wrap">
                                            <input
                                                type="checkbox"
                                                className="k-checkbox k-checkbox-md k-rounded-md"
                                                id="remember-me"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                            />
                                            <label className="k-checkbox-label" htmlFor="remember-me">
                                                Ține-mă minte
                                            </label>
                                        </span>
                                    </div>
                                    <button type="button" className="forgot-password-btn fs-6">
                                        Ai uitat parola?
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    className="k-button k-button-md k-button-solid k-button-solid-primary k-rounded-md login-submit-btn"
                                    disabled={loading || !password}
                                >
                                    <span className="k-button-text">{loading ? 'Se încarcă...' : 'Intră în cont'}</span>
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
